import React, { useState } from 'react';
import { AppBar, Badge, styled } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { Menu as MenuIcon, NotificationsOutlined } from '@mui/icons-material';
import { Colors } from 'library';
import TandemLogo from 'assets/images/NavyLogo.svg';
import { Avatar, DropdownMenu, DropdownMenuItem } from 'library/ui';
import { useNotificationContext } from 'parent-portal/notifications/NotificationContext/NotificationContext';
import ImpersonationButton from './ImpersonationButton';

interface LayoutTopBarProps {
    small: boolean;
    onToggleSidebar: () => void;
    initials: string;
    avatarImageUrl?: string | null;
    accountMenuItems?: DropdownMenuItem[];
    showNotificationBell?: boolean;
}

export function LayoutTopBar({
    small,
    onToggleSidebar,
    initials,
    avatarImageUrl,
    accountMenuItems,
    showNotificationBell = false,
}: LayoutTopBarProps) {
    const [menuOpen, setMenuOpen] = useState(false);

    return (
        <StyledAppBar position="fixed">
            <Bar small={small}>
                <LeftSection>
                    <TandemLogo htmlColor={Colors.primary} style={{ height: 72, width: 72, flexShrink: 0 }} />
                    {small && (
                        <MenuButton onClick={onToggleSidebar} aria-label="toggle sidebar">
                            <MenuIcon sx={{ color: Colors.primary, fontSize: 22 }} />
                        </MenuButton>
                    )}
                    <ImpersonationButton />
                </LeftSection>
                <RightSection>
                    {showNotificationBell && <NotificationBell />}
                    {accountMenuItems && accountMenuItems.length > 0 ? (
                        <DropdownMenu
                            open={menuOpen}
                            onClose={() => setMenuOpen(false)}
                            items={accountMenuItems}
                            trigger={
                                <Avatar
                                    id="profile-pic"
                                    initials={initials}
                                    imageUrl={avatarImageUrl}
                                    size={36}
                                    onClick={() => setMenuOpen((v) => !v)}
                                />
                            }
                        />
                    ) : (
                        <Avatar initials={initials} imageUrl={avatarImageUrl} size={36} />
                    )}
                </RightSection>
            </Bar>
        </StyledAppBar>
    );
}

function NotificationBell() {
    const history = useHistory();
    const { unacknowledgedCount, acknowledgeNotifications } = useNotificationContext();

    const onClick = () => {
        acknowledgeNotifications();
        history.push('/notifications');
    };

    return (
        <BellButton onClick={onClick} aria-label="notifications">
            <Badge badgeContent={unacknowledgedCount} color="error" overlap="circular" max={9}>
                <NotificationsOutlined sx={{ color: Colors.primary, fontSize: 28 }} />
            </Badge>
        </BellButton>
    );
}

const StyledAppBar = styled(AppBar)({
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    backgroundColor: Colors.white,
    zIndex: 1201,
});

const Bar = styled('div')<{ small: boolean }>(({ small }) => ({
    alignItems: 'center',
    display: 'flex',
    height: small ? 50 : 60,
    justifyContent: 'space-between',
    padding: '0 16px',
}));

const LeftSection = styled('div')({
    alignItems: 'center',
    display: 'flex',
    gap: 8,
});

const RightSection = styled('div')({
    alignItems: 'center',
    display: 'flex',
    gap: 16,
});

const MenuButton = styled('button')({
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 4,
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
        backgroundColor: Colors.fill,
    },
});

const BellButton = styled('button')({
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    outline: 'none',
    '&:focus, &:focus-visible': { outline: 'none', boxShadow: 'none' },
});
